import BigNumber from 'bignumber.js'

import { trimCharEnd } from '@/utils/strings'

import { bigNumberify } from '../../utils/bigNumber'
import { Percentage } from '../types/NumericValues'
import { formatCurrency } from '@coingecko/cryptoformat'

export interface FormatPercentageOptions {
  skipSign?: boolean
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}
export function formatPercentage(
  percentage: Percentage | undefined,
  { skipSign, minimumFractionDigits, maximumFractionDigits }: FormatPercentageOptions = {},
): string {
  if (percentage === undefined) {
    return '—'
  }

  if (percentage.gt(0) && percentage.lt(0.0001)) {
    return skipSign ? '<0.01' : '<0.01%'
  }

  const percentageFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: maximumFractionDigits ?? 2,
    minimumFractionDigits: minimumFractionDigits ?? 2,
  })

  const value = percentage.multipliedBy(100).toNumber()
  return `${percentageFormatter.format(value)}${skipSign ? '' : '%'}`
}

const healthFactorFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})
export function formatHealthFactor(
  healthFactor: BigNumber | undefined,
  { truncateLarge = false }: { truncateLarge?: boolean } = {},
): string {
  if (healthFactor === undefined) {
    return '0.0'
  }
  if (!healthFactor.isFinite()) {
    return String.fromCharCode(0x221e)
  }

  const hf = bigNumberify(healthFactor).toNumber()

  if (truncateLarge && hf > 4) {
    return '4+'
  }

  return healthFactorFormatter.format(hf)
}

export function formFormat(value: BigNumber, precision = 2): string {
  const roundedValue = value.toFixed(precision, BigNumber.ROUND_DOWN)

  // avoid trailing zeroes
  // @note: we can't use a Intl.formatter here because it doesn't support rounding modes :/
  return trimCharEnd(trimCharEnd(roundedValue, '0'), '.')
}

export function findSignificantPrecision(
  _unitPriceUsd: BigNumber,
  desiredPrecisionOfUsd = 2, // 0.01 = cents
): number {
  const unitPriceUsd = Number(_unitPriceUsd)
  return Math.max(Math.floor(Math.log10(unitPriceUsd)) + desiredPrecisionOfUsd, 0)
}

export function truncateWithEllipsis(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function formatCurrencyWithSmartTrailingZeros(value: number): string {
  // Use formatCurrency with 6 decimal places
  const formatted = formatCurrency(value, 'USD', 'en', false, {
    decimalPlaces: 6,
  })

  // Extract the numeric part (remove currency symbol and any spaces)
  const match = formatted.match(/[\d.,]+/)
  if (!match) return formatted

  const numericPart = match[0]
  const currencySymbol = formatted.replace(numericPart, '').trim()

  // Split into integer and decimal parts
  const parts = numericPart.split('.')
  if (parts.length === 1) {
    // No decimal part, add .00
    return `${currencySymbol}${parts[0]}.00`
  }

  const [integerPart, decimalPart] = parts

  // Remove trailing zeros but keep at least 2 decimal places
  let trimmedDecimal = decimalPart?.replace(/0+$/, '')
  if (trimmedDecimal && trimmedDecimal.length < 2) {
    trimmedDecimal = trimmedDecimal.padEnd(2, '0')
  }

  return trimmedDecimal ? `${currencySymbol}${integerPart}.${trimmedDecimal}` : `${currencySymbol}${integerPart}`
}

export function contractNameOverride(name: string): string {
  if (name === 'y��\x02J�6�\x02�EVU\rܦ��h') {
    return 'stLOOP'
  }
  return name
}
